import { useEffect, useId, useRef, type ReactNode } from "react";
import { cn } from "../lib/cn";

/**
 * Small centred dialog for destructive actions — the design's confirm alert.
 *
 * `role="alertdialog"` rather than plain dialog: it tells assistive tech this
 * interrupts for a consequence, and the description is announced with it. For
 * "delete this product" that difference matters.
 *
 * The confirm button is focused on open, but it is the CANCEL button that the
 * Esc key and backdrop click resolve to — a destructive dialog must never be
 * completable by accident.
 */
export type ConfirmDialogProps = {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  /** Destructive styling. Default true — this dialog exists for destruction. */
  destructive?: boolean;
  /**
   * The confirmed action is in flight.
   *
   * BOTH buttons go disabled, not just confirm. A destructive action is
   * usually not idempotent, and the dialog stays mounted while the request
   * runs — so a second click on "Delete" sends a second delete, and a click on
   * "Cancel" closes the dialog over a mutation that is still going to land.
   * Neither is recoverable by the person who did it.
   *
   * It does NOT swap the label for a spinner: `confirmLabel` is the caller's
   * copy and a library cannot invent "Deleting…" in their language.
   */
  busy?: boolean;
  className?: string;
};

export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  destructive = true,
  busy = false,
  className,
}: ConfirmDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      role="alertdialog"
      aria-describedby={description ? descriptionId : undefined}
      onCancel={(e) => {
        e.preventDefault();
        // Esc and the backdrop are the same door as the Cancel button, so they
        // are shut for the same reason while the action is in flight.
        if (!busy) onCancel();
      }}
      onClick={(e) => {
        if (e.target === ref.current && !busy) onCancel();
      }}
      className={cn(
        "m-auto w-[min(380px,calc(100vw-32px))] rounded-[12px] p-0",
        "border border-border2 bg-field text-text shadow-menu",
        "animate-pop-in backdrop:bg-transparent",
        className,
      )}
    >
      <div className="flex flex-col gap-2 px-[18px] pt-4">
        <h2 id={titleId} className="text-section font-semibold text-text">{title}</h2>
        {description ? (
          <p id={descriptionId} className="text-secondary leading-[1.6] text-sec">
            {description}
          </p>
        ) : null}
      </div>

      <div className="mt-4 flex justify-end gap-2 px-[18px] pb-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className={cn(
            "cursor-pointer rounded-control border border-border2 px-3 py-1.5",
            "text-secondary text-text3 hover:bg-raised2",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          aria-busy={busy || undefined}
          className={cn(
            "cursor-pointer rounded-control border-none px-3.5 py-[7px] text-secondary font-semibold",
            "disabled:cursor-not-allowed disabled:opacity-50",
            destructive
              ? "bg-danger text-white hover:bg-danger-hover"
              : "bg-brand text-on-brand hover:bg-brand-hover",
          )}
        >
          {confirmLabel}
        </button>
      </div>
    </dialog>
  );
}
